import { emptySplitApi } from './api.slice';
import { isAnyOf, PayloadAction } from '@reduxjs/toolkit';
import { Authenticator, ContactType, OnboardingLocale } from './types';

export interface Contact {
    contactType: ContactType;
    contactId: string;
    displayName: string; // confirm this is returned from all contacts apis
}

export interface ResetPasswordVerifyTotpResponse {
    contacts: Array<Contact>;
}

interface InvitationResponse {
    email: string;
    contactId: string;
    appName: string;
    appId: string;
    isEnvApp: boolean;
    locale?: string;
}

interface HasApplication {
    appId?: string;
    isEnvApp?: boolean;
}

type TrustedDeviceResponse = HasApplication;

interface VerifyLoginTotpResponse extends HasApplication {
    isTrusted: boolean;
}

interface EnrollmentEmailSubmitResponse {
    userId: string;
    userEmail: string;
    userFirstName: string;
    userLastName: string;
}

interface EnrollmentPhoneInitResponse extends HasApplication {
    contactId: string;
}

interface EnrollmentPasswordSubmitResponse {
    userId: string;
    userEmail: string;
    unverifiedPhoneContact: {
        number: string;
        countryCode: string;
    };
}

export interface MaskedContact {
    contacts: Array<Contact>;
    authenticatorEnabled: boolean;
}

export interface RecoverAccountResponse {
    isVcrEnvironment: boolean;
    maskedContact: MaskedContact;
}

export type RecoverAccountType = 'recover';

export interface RecoverAccountOptions {
    recoverAccount?: RecoverAccountType;
}

interface ResetPasswordSubmitOtpInput extends RecoverAccountOptions {
    email: string;
    passcode: string;
}

interface ResetPasswordOtpInit extends RecoverAccountOptions {
    email: string;
}
interface VerifyNewAuthenticatorInput extends RecoverAccountOptions {
    passcode: string;
}

// These paths are exclusively used by the publicly facing auth module
export const AuthApiPaths = {
    invitation: '/auth/v1/login/info',

    // Not currently used, needs uptake in respective component
    initEmailLogin: '/auth/v1/login/email',
    loginPassword: '/auth/v1/login/password',
    verifyLoginTotp: '/auth/v1/mfa/authenticator/verify',
    trustedDevice: '/auth/v1/trustedDevice',
    enrollmentInvitation: '/auth/v1/invitation',

    // invitation flow
    enrollmentEmailInit: '/auth/v1/enrollment/email/init',
    enrollmentEmailSubmit: '/auth/v1/enrollment/email/submit',
    enrollmentPasswordSubmit: '/auth/v1/enrollment/password',
    enrollmentAuthenticatorRegister: '/auth/v1/enrollment/authenticator/register',
    enrollmentAuthenticatorVerify: '/auth/v1/enrollment/authenticator/verify',
    enrollmentPhoneSkip: '/auth/v1/enrollment/phone/skip',
    enrollmentPhoneInit: '/auth/v1/enrollment/phone/init',
    enrollmentPhoneSubmit: '/auth/v1/enrollment/phone/submit',
    enrollmentLocaleSubmit: '/auth/v1/enrollment/locale',

    mfaEmailInit: '/auth/v1/mfa/email/init',
    mfaEmailVerify: '/auth/v1/mfa/email/verify',
    mfaPhoneInit: '/auth/v1/mfa/phone/init',
    mfaPhoneVerify: '/auth/v1/mfa/phone/verify',

    // reset password
    resetPasswordVerifyTotp: '/auth/v1/passwordreset/verifyTotp',
    resetPasswordVerifyPhoneOTP: '/auth/v1/passwordreset/phone/submitOtp',
    resetPasswordUserConsentForPhoneOtp: '/auth/v1/passwordreset/phone/consent',
    resetPassword: '/auth/v1/passwordreset/password',
    recoverAccount: '/auth/v1/passwordreset/recoverAccount',
    resetPasswordInit: '/auth/v1/passwordreset/init',
    resetPasswordSubmitOtp: '/auth/v1/passwordreset/email/submitOtp',
    resendPhoneOtp: '/auth/v1/passwordreset/phone/resendOtp',
    resendEmailOtp: '/auth/v1/passwordreset/email/resendOtp',
    registerNewAuthenticator: '/auth/v1/mfa/authenticator/registerNew',
    verifyNewAuthenticator: '/auth/v1/mfa/authenticator/verifyNew'
};

export const authApiSlice = emptySplitApi.enhanceEndpoints({ addTagTypes: ['Invitation'] }).injectEndpoints({
    endpoints: builder => ({
        verifyLoginTotp: builder.mutation<VerifyLoginTotpResponse, { passcode: string }>({
            query: ({ passcode }) => ({
                method: 'post',
                url: AuthApiPaths.verifyLoginTotp,
                body: {
                    passcode
                }
            })
        }),
        trustedDevice: builder.mutation<TrustedDeviceResponse, { trustThisDevice: boolean }>({
            query: ({ trustThisDevice }) => ({
                method: 'post',
                url: AuthApiPaths.trustedDevice,
                body: {
                    trustThisDevice
                }
            })
        }),
        getInvitation: builder.query<InvitationResponse, void>({
            query: () => AuthApiPaths.invitation
        }),
        recoverAccount: builder.mutation<RecoverAccountResponse, void>({
            query: () => ({
                method: 'post',
                url: AuthApiPaths.recoverAccount
            })
        }),
        initEmailLogin: builder.mutation({
            query: body => ({
                body,
                method: 'post',
                url: AuthApiPaths.initEmailLogin
            })
        }),
        loginPassword: builder.mutation({
            query: body => ({
                body,
                method: 'post',
                url: AuthApiPaths.loginPassword
            })
        }),
        resetPasswordVerifyPhoneOtp: builder.mutation({
            query: ({ contactId, passcode }) => ({
                method: 'post',
                url: AuthApiPaths.resetPasswordVerifyPhoneOTP,
                body: { contactId, passcode }
            })
        }),
        resetPasswordUserConsentForPhoneOtp: builder.mutation({
            query: body => ({
                body,
                method: 'post',
                url: AuthApiPaths.resetPasswordUserConsentForPhoneOtp
            })
        }),
        resendPhoneResetPassword: builder.mutation({
            query: ({ contactId }) => ({
                method: 'post',
                url: AuthApiPaths.resendPhoneOtp,
                body: { contactId }
            })
        }),
        resendEmailResetPassword: builder.mutation({
            query: ({ contactId }) => ({
                method: 'post',
                url: AuthApiPaths.resendEmailOtp,
                body: { contactId }
            })
        }),
        resetPassword: builder.mutation({
            query: ({ email, password }) => ({
                method: 'post',
                url: AuthApiPaths.resetPassword,
                body: {
                    email,
                    password
                }
            })
        }),
        resetPasswordInit: builder.mutation<void, ResetPasswordOtpInit>({
            query: ({ email, recoverAccount }) => ({
                method: 'post',
                url: AuthApiPaths.resetPasswordInit,
                body: {
                    email,
                    recoverAccount
                }
            })
        }),
        resetPasswordSubmitOtp: builder.mutation<void, ResetPasswordSubmitOtpInput>({
            query: ({ email, passcode }) => ({
                method: 'post',
                url: AuthApiPaths.resetPasswordSubmitOtp,
                body: {
                    email,
                    passcode
                }
            })
        }),
        registerNewAuthenticator: builder.mutation<Authenticator, void>({
            query: () => ({
                method: 'post',
                url: AuthApiPaths.registerNewAuthenticator
            })
        }),
        verifyNewAuthenticator: builder.mutation<void, VerifyNewAuthenticatorInput>({
            query: ({ passcode }) => ({
                method: 'post',
                url: AuthApiPaths.verifyNewAuthenticator,
                body: { passcode }
            })
        }),
        // enrollment queries
        enrollmentEmailInit: builder.mutation<void, { contactId: string; showToast?: boolean }>({
            query: ({ contactId }) => ({
                method: 'post',
                url: AuthApiPaths.enrollmentEmailInit,
                body: { contactId }
            })
        }),
        enrollmentEmailSubmit: builder.mutation<EnrollmentEmailSubmitResponse, { contactId: string; passcode: string }>(
            {
                query: ({ contactId, passcode }) => ({
                    method: 'post',
                    url: AuthApiPaths.enrollmentEmailSubmit,
                    body: { contactId, passcode }
                })
            }
        ),
        enrollmentPasswordSubmit: builder.mutation<
            EnrollmentPasswordSubmitResponse,
            { password: string; userId: string; userFirstName: string; userLastName: string }
        >({
            query: ({ password, userId, userFirstName, userLastName }) => ({
                method: 'post',
                url: AuthApiPaths.enrollmentPasswordSubmit,
                body: { password, userId, userFirstName, userLastName }
            })
        }),
        enrollmentAuthenticatorRegister: builder.mutation<Authenticator, void>({
            query: () => ({
                method: 'post',
                url: AuthApiPaths.enrollmentAuthenticatorRegister
            })
        }),
        enrollmentAuthenticatorVerify: builder.mutation<void, { passcode: string }>({
            query: ({ passcode }) => ({
                method: 'post',
                url: AuthApiPaths.enrollmentAuthenticatorVerify,
                body: {
                    passcode
                }
            })
        }),
        enrollmentPhoneSkip: builder.mutation<HasApplication | void, void>({
            query: () => ({
                method: 'post',
                url: AuthApiPaths.enrollmentPhoneSkip
            })
        }),
        enrollmentPhoneInit: builder.mutation<
            EnrollmentPhoneInitResponse,
            { countryCode: string; number: string; showToast?: boolean }
        >({
            query: ({ countryCode, number }) => ({
                method: 'post',
                url: AuthApiPaths.enrollmentPhoneInit,
                body: {
                    phone: {
                        countryCode,
                        number
                    }
                }
            })
        }),
        enrollmentPhoneSubmit: builder.mutation<HasApplication | void, { contactId: string; passcode: string }>({
            query: ({ passcode, contactId }) => ({
                method: 'post',
                url: AuthApiPaths.enrollmentPhoneSubmit,
                body: {
                    contactId,
                    passcode
                }
            })
        }),
        enrollmentLocale: builder.mutation<OnboardingLocale | void, { locale: string }>({
            query: ({ locale }) => ({
                method: 'put',
                url: AuthApiPaths.enrollmentLocaleSubmit,
                body: {
                    locale
                }
            }),
            invalidatesTags: ['Invitation']
        }),
        enrollmentInvitation: builder.mutation<void, { email: string }>({
            query: ({ email }) => ({
                method: 'post',
                url: AuthApiPaths.enrollmentInvitation,
                body: {
                    email
                }
            })
        }),
        resetPasswordVerifyTotp: builder.mutation<ResetPasswordVerifyTotpResponse, { email: string; passcode: string }>(
            {
                query: ({ email, passcode }) => ({
                    method: 'post',
                    url: AuthApiPaths.resetPasswordVerifyTotp,
                    body: {
                        email,
                        passcode
                    }
                })
            }
        )
    })
});

const isLoginTotpMatch = (
    action: PayloadAction<VerifyLoginTotpResponse>
): action is PayloadAction<VerifyLoginTotpResponse> => {
    const isMatch = authApiSlice.endpoints.verifyLoginTotp.matchFulfilled(action);
    // if the device is not trusted the component will handle the following steps, see LoginTOTPVerify.tsx.
    return isMatch && action.payload.isTrusted;
};

export const isLoginMatch = isAnyOf(
    isLoginTotpMatch,
    authApiSlice.endpoints.enrollmentPhoneSubmit.matchFulfilled,
    authApiSlice.endpoints.trustedDevice.matchFulfilled,
    authApiSlice.endpoints.enrollmentPhoneSkip.matchFulfilled
);

export const {
    useGetInvitationQuery,
    useRecoverAccountMutation,
    useLoginPasswordMutation,
    useVerifyLoginTotpMutation,
    useTrustedDeviceMutation,
    useInitEmailLoginMutation,
    useResetPasswordVerifyTotpMutation,
    useResetPasswordUserConsentForPhoneOtpMutation,
    useResetPasswordVerifyPhoneOtpMutation,
    useResetPasswordInitMutation,
    useResetPasswordSubmitOtpMutation,
    useResendPhoneResetPasswordMutation,
    useResendEmailResetPasswordMutation,
    useResetPasswordMutation,
    useRegisterNewAuthenticatorMutation,
    useVerifyNewAuthenticatorMutation,
    useEnrollmentEmailInitMutation,
    useEnrollmentEmailSubmitMutation,
    useEnrollmentPasswordSubmitMutation,
    useEnrollmentAuthenticatorRegisterMutation,
    useEnrollmentAuthenticatorVerifyMutation,
    useEnrollmentPhoneSkipMutation,
    useEnrollmentPhoneInitMutation,
    useEnrollmentPhoneSubmitMutation,
    useEnrollmentInvitationMutation,
    useEnrollmentLocaleMutation
} = authApiSlice;
